import { useRef, useEffect, useMemo } from "react"
import { langStore } from "@/stores/lang-store"
import dayjs from "dayjs"
import { heatmapBundle } from "./leak-heatmap-const"

// [상위 컴포넌트] : 데이터 가공과 레이아웃(CSS Grid)만 담당
const LeakHeatmapCalendar = ({
  startDate,
  endDate,
  dailyData,
  maxVal,
  onMonthClick,
  color = "#2563eb"
}) => {
  const months = useMemo(() => {
    if (!startDate || !endDate) return []

    const start = dayjs(startDate)
    const end = dayjs(endDate)

    const list = []

    let current = start.startOf("month")
    const endMonth = end.startOf("month")

    while (current.isBefore(endMonth) || current.isSame(endMonth)) {
      list.push(current.toDate())
      current = current.add(1, "month")
    }

    return list
  }, [startDate, endDate])

  return (
    <div className="-mx-2 grid grid-cols-2 gap-x-6 gap-y-8 bg-[#f8f9fa] p-3 rounded-xl border border-gray-100">
      {months.map((date, idx) => (
        <MonthCanvas
          key={idx}
          date={date}
          endDate={endDate}
          dailyData={dailyData}
          maxVal={maxVal}
          color={color}
          onClick={() => onMonthClick?.(date)}
        />
      ))}
    </div>
  )
}

// [하위 컴포넌트] : 딱 1개 월만 그리는 캔버스
const MonthCanvas = ({ date, dailyData, endDate, maxVal, color, onClick }) => {
  const canvasRef = useRef(null)
  const containerRef = useRef(null)

  const { lang } = langStore()

  useEffect(() => {
    const canvas = canvasRef.current
    const container = containerRef.current

    if (!canvas || !container) return

    const ctx = canvas.getContext("2d")

    if (!ctx) return

    // 스타일 상수
    const DAY_GAP = 2
    const FONT_SIZE_TITLE = 14
    const FONT_SIZE_YEAR = 12
    const TITLE_MARGIN_BOTTOM = 8

    const render = () => {
      const width = container.clientWidth

      if (width <= 0) return

      // 원 지름 계산 (7일 기준)
      const circleSize = (width - DAY_GAP * 6) / 7

      // 높이 계산 (제목 + 최대 6주)
      const height = FONT_SIZE_TITLE + TITLE_MARGIN_BOTTOM + circleSize * 6 + DAY_GAP * 5

      // DPI 대응
      const dpr = window.devicePixelRatio || 1

      canvas.width = width * dpr
      canvas.height = height * dpr

      canvas.style.width = `${width}px`
      canvas.style.height = `${height}px`

      // transform 초기화
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      ctx.scale(dpr, dpr)

      ctx.clearRect(0, 0, width, height)

      // 날짜 정보
      const currentMonth = dayjs(date)

      const year = currentMonth.year()
      const monthIndex = currentMonth.month()

      const daysInMonth = currentMonth.daysInMonth()

      const startDay = currentMonth.startOf("month").day()

      // 제목
      const monthText = heatmapBundle.months[lang][monthIndex]

      ctx.fillStyle = "#374151"
      ctx.font = `bold ${FONT_SIZE_TITLE}px sans-serif`
      ctx.textAlign = "left"
      ctx.textBaseline = "top"

      ctx.fillText(monthText, 0, 0)

      const monthTextWidth = ctx.measureText(monthText).width

      ctx.fillStyle = "#9ca3af"
      ctx.font = `normal ${FONT_SIZE_YEAR}px sans-serif`

      const yearGap = lang === "ko" ? 4 : 6

      ctx.fillText(`${year}`, monthTextWidth + yearGap, 2)

      // 날짜 렌더링
      const gridStartY = FONT_SIZE_TITLE + TITLE_MARGIN_BOTTOM

      for (let i = 0; i < daysInMonth; i++) {
        const currentDay = i + 1

        const gridIndex = startDay + i

        const col = gridIndex % 7
        const row = Math.floor(gridIndex / 7)

        const circleX = col * (circleSize + DAY_GAP)

        const circleY = gridStartY + row * (circleSize + DAY_GAP)

        const radius = circleSize / 2

        if (radius <= 0) continue
        const date = dayjs(`${year}-${monthIndex + 1}-${currentDay}`)
        const dateKey = date.format("YYYY-MM-DD")

        const value = dailyData?.[dateKey] || 0
        const isBeforeAndSameDate =
          dayjs(date).isBefore(dayjs(endDate), "day") || dayjs(date).isSame(dayjs(endDate), "day")
        let fillStyle = "#efefef"

        if (isBeforeAndSameDate) {
          const ratio = maxVal > 0 ? Math.min(value / maxVal, 1) : 0
          if (ratio === 0) fillStyle = "#d5e7eb"
          else {
            const opacity = 0.3 + ratio * 0.7
            fillStyle = hexToRgba(color, opacity)
          }
        }

        ctx.beginPath()

        ctx.arc(circleX + radius, circleY + radius, radius, 0, 2 * Math.PI)

        ctx.fillStyle = fillStyle
        ctx.fill()
        ctx.closePath()
      }
    }

    render()

    const resizeObserver = new ResizeObserver(() => render())

    resizeObserver.observe(container)

    return () => {
      resizeObserver.disconnect()
    }
  }, [date, dailyData, maxVal, lang, color, endDate])

  return (
    <div
      ref={containerRef}
      onClick={onClick}
      className="cursor-pointer transition-transform duration-200 hover:scale-105 active:scale-95"
    >
      <canvas
        ref={canvasRef}
        style={{
          display: "block",
          width: "100%"
        }}
      />
    </div>
  )
}

const hexToRgba = (hex, alpha) => {
  const normalized = hex.replace("#", "")
  const full =
    normalized.length === 3
      ? normalized
          .split("")
          .map((char) => char + char)
          .join("")
      : normalized

  const r = parseInt(full.slice(0, 2), 16)
  const g = parseInt(full.slice(2, 4), 16)
  const b = parseInt(full.slice(4, 6), 16)

  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

export default LeakHeatmapCalendar
